import { useSelector } from 'react-redux';

import {
  FF_HOLIDAY_BANNER_CN,
  FF_HOLIDAY_BANNER_IN,
  FF_HOLIDAY_BANNER_VN,
} from 'constants/features';
import { usePeertransferFlags } from 'hooks/usePeertransferFlags/usePeertransferFlags';
import {
  isPayingFromChina,
  isPayingFromIndia,
  isPayingFromVietnam,
} from 'selectors';
import { useTranslations } from 'utils/translations/useTranslations';

import './HolidayBanner.scss';

function Vietnam() {
  const i18n = useTranslations();

  if (i18n.currentLocale === 'en') {
    return (
      <div className="HolidayBanner">
        <div className="HolidayBanner-content">
          <div className="HolidayBanner-title">Important note:</div>
          <p className="HolidayBanner-description">
            Please be advised, the period from <b>30th April to 1st May 2025</b>{' '}
            is a public holiday period in Vietnam. Therefore, bank transfer
            payments received during this public holiday period will only be
            processed, and status will only be updated, on or after{' '}
            <b>2nd May 2025</b> (if payments are made to the recipient bank{' '}
            <b>'VP Bank'</b>, they will be processed on or after{' '}
            <b>5th May 2025</b>).
            <br />
            We recommend you to schedule your payment plan prior to avoid any
            delays of your payment. We apologize for the inconvenience caused,
            and thank you for your patience. If you have any questions about
            your payment during the holidays, feel free to contact us through
            the{' '}
            <a
              target="_blank"
              href="https://help.flywire.com/hc/en-us"
              rel="noreferrer"
            >
              Help & Support Center
            </a>
            !
            <br />
            We wish you a nice holiday!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="HolidayBanner">
      <div className="HolidayBanner-content">
        <div className="HolidayBanner-title">Thông báo quan trọng:</div>
        <p className="HolidayBanner-description">
          Xin lưu ý rằng khoảng thời gian từ ngày{' '}
          <b>30 tháng 4 đến hết ngày 1 tháng 5 năm 2025</b> là thời gian nghỉ lễ
          tại Việt Nam. Do đó, các khoản thanh toán chuyển khoản ngân hàng nhận
          được trong thời gian nghỉ lễ này sẽ chỉ được xử lý và trạng thái thanh
          toán sẽ chỉ được cập nhật vào hoặc sau <b>ngày 2 tháng 5 năm 2025</b>{' '}
          (Nếu thanh toán được thực hiện đến ngân hàng nhận <b>'VP Bank'</b> sẽ
          được xử lý vào hoặc sau <b>ngày 5 tháng 5 năm 2025</b>).
          <br />
          Chúng tôi khuyến nghị bạn lên kế hoạch thanh toán trước để tránh bất
          kỳ sự chậm trễ nào trong việc thanh toán. Chúng tôi xin lỗi về sự bất
          tiện này và cảm ơn bạn vì sự kiên nhẫn. Nếu bạn có bất kỳ câu hỏi nào
          về việc thanh toán trong kỳ nghỉ lễ, vui lòng liên hệ với chúng tôi
          qua{' '}
          <a
            target="_blank"
            href="https://help.flywire.com/hc/vi"
            rel="noreferrer"
          >
            Trung tâm trợ giúp & hỗ trợ
          </a>
          !
          <br />
          Chúng tôi chúc bạn một kỳ nghỉ tốt đẹp!
        </p>
      </div>
    </div>
  );
}

function China() {
  const i18n = useTranslations();

  if (i18n.currentLocale === 'en') {
    return (
      <div className="HolidayBanner">
        <div className="HolidayBanner-content">
          <div className="HolidayBanner-title">Important note:</div>
          <p className="HolidayBanner-description">
            Please be advised that from May 1st, 2025 to May 5th, 2025 is the
            Labor Day Holiday, the payment you made during this period
            (including April 30th) will only be processed or updated on or after
            May 6th, 2025.
            <br />
            We recommend you schedule your payment plan prior to avoiding any
            delays of your payment. We apologize for the inconvenience caused
            and thank you for your patience. If you have any questions about
            your payment during the holidays, feel free to contact us through
            the "
            <a
              target="_blank"
              href="https://help.flywire.com/hc/en-us"
              rel="noreferrer"
            >
              Help Center
            </a>
            "!
            <br />
            Flywire wishes you a happy holiday!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="HolidayBanner">
      <div className="HolidayBanner-content">
        <div className="HolidayBanner-title">重要提醒：</div>
        <p className="HolidayBanner-description">
          请知悉，因 2025年5月1日至
          2025年5月5日为中国劳动节假日，在此期间您所支付的款项（包含4月30日）将仅会在
          2025年5月6日 或之后开始处理，并更新付款状态。
          <br />
          您可合理安排您的付款计划，以避免出现费用缴纳的延误。由此造成的不便，我们深表歉意，并感谢您的耐心等待。如您在节假日期间有关于付款的问题，您仍可通过“
          <a
            target="_blank"
            href="https://help.flywire.com/hc/zh-cn"
            rel="noreferrer"
          >
            帮助页面
          </a>
          ”尝试与我们联系咨询！
          <br />
          飞汇在此祝您假期愉快！
        </p>
      </div>
    </div>
  );
}

function India() {
  const i18n = useTranslations();

  if (i18n.currentLocale === 'en') {
    return (
      <div className="HolidayBanner">
        <div className="HolidayBanner-content">
          <div className="HolidayBanner-title">Important note:</div>
          <p className="HolidayBanner-description">
            As March <strong>29th and 30th</strong> fall on{' '}
            <strong>Saturday and Sunday respectively</strong> and{' '}
            <strong>March 31st</strong> being a <strong>bank holiday</strong>,
            payments created during this period will be processed only on{' '}
            <strong>April 2nd, 2025</strong>.
            <br />
            Additionally, <strong>NEFT</strong>, <strong>RTGS</strong>,{' '}
            <strong>and Net Banking services</strong> will not be available in
            India on <strong>April 1st</strong>.
            <br />
            Please note that the <strong>Financial Year change</strong> may
            impact the <strong>Tax Collected at Source (TCS) on</strong> your
            payment. This could require our payment partners to contact
            students/parents to complete the payment.
            <br />
            <strong>
              For faster processing, we recommend creating your booking on April
              2nd
            </strong>
            . Flywire prioritizes students' interests in everything we do,
            ensuring dedicated support for all payers.
          </p>
        </div>
      </div>
    );
  }
}

China.displayName = 'HolidayBanner.China';
India.displayName = 'HolidayBanner.India';
Vietnam.displayName = 'HolidayBanner.Vietnam';

function HolidayBanner() {
  const fromChina = useSelector(isPayingFromChina);
  const fromIndia = useSelector(isPayingFromIndia);
  const fromVietnam = useSelector(isPayingFromVietnam);

  const shouldDisplayHolidayBanner = fromChina || fromIndia || fromVietnam;

  const { data: flags } = usePeertransferFlags(
    [FF_HOLIDAY_BANNER_CN, FF_HOLIDAY_BANNER_IN, FF_HOLIDAY_BANNER_VN],
    {
      enabled: shouldDisplayHolidayBanner,
    },
  );

  if (!flags) {
    return null;
  }

  const showChineseBanner = fromChina && flags[FF_HOLIDAY_BANNER_CN];
  const showIndianBanner = fromIndia && flags[FF_HOLIDAY_BANNER_IN];
  const showVietnameseBanner = fromVietnam && flags[FF_HOLIDAY_BANNER_VN];

  return (
    <>
      {showChineseBanner && <China />}
      {showIndianBanner && <India />}
      {showVietnameseBanner && <Vietnam />}
    </>
  );
}

export { HolidayBanner };
